import Cookies from 'js-cookie';
import { getAuthorization, setAuthorization, updateLoggedinUser } from './auth';
import { resetPosthog } from '../services/posthog';

export type RequestConfig = {
    url: string;
    method?: string;
    params?: any;
    data?: any;
    headers?: Record<string, any>;
    signal?: AbortSignal;
    responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
    timeout?: number;
    baseURL?: string;
    credentials?: RequestCredentials;
    authorization?: boolean;
    csrf?: boolean;
    handleErrors?: boolean;
    unwrap?: boolean;
    _tokenTopUpRetry?: boolean;
    /** Batch callers refresh their own affected list after all writes settle. */
    invalidateQueries?: boolean;
    query?: { staleTime?: number; gcTime?: number };
};
export type HttpResponse<T = any> = {
    data: T; status: number; statusText: string;
    headers: Record<string, string>; config: RequestConfig;
};
export class HttpError<T = any> extends Error {
    readonly isHttpError = true;
    readonly status?: number;
    constructor(message: string, public config: RequestConfig, public response?: HttpResponse<T>) {
        super(message); this.name = 'HttpError'; this.status = response?.status;
    }
}
export class RequestCancelledError extends Error {
    readonly code = 'ERR_CANCELED';
    constructor(message = 'Request cancelled') { super(message); this.name = 'CanceledError'; }
}
export const isHttpError = (error: unknown): error is HttpError => Boolean((error as any)?.isHttpError);
export const requestSettings = { baseURL: import.meta.env?.VITE_API_URL || '' };

export function encodeRequestUrl(config: RequestConfig) {
    const base = config.baseURL ?? requestSettings.baseURL;
    const url = /^https?:\/\//i.test(config.url) ? config.url : `${base.replace(/\/$/, '')}/${config.url.replace(/^\//, '')}`;
    const params = new URLSearchParams();
    const append = (key: string, value: any) => {
        if (value === null || value === undefined) return;
        if (value instanceof Date) { params.append(key, value.toISOString()); return; }
        if (Array.isArray(value)) { value.forEach((item, index) => append(`${key}[${item && typeof item === 'object' ? index : ''}]`, item)); return; }
        if (typeof value === 'object') { Object.entries(value).forEach(([child, item]) => append(`${key}[${child}]`, item)); return; }
        params.append(key, String(value));
    };
    if (config.params instanceof URLSearchParams) config.params.forEach((value, key) => params.append(key, value));
    else Object.entries(config.params || {}).forEach(([key, value]) => append(key, value));
    const query = params.toString();
    return url + (query ? `${url.includes('?') ? '&' : '?'}${query}` : '');
}

export function prepareRequest(config: RequestConfig): RequestConfig {
    const headers: Record<string, string> = {};
    Object.entries(config.headers || {}).forEach(([key, value]) => { if (value !== undefined && value !== null) headers[key] = String(value); });
    const normalized = new Headers(headers);
    if (!normalized.has('Accept')) normalized.set('Accept', 'application/json');
    if (config.authorization !== false && !normalized.has('Authorization')) {
        const authorization = getAuthorization();
        if (authorization) normalized.set('Authorization', authorization);
    }
    if (!normalized.has('X-Frontend-Path') && typeof window !== 'undefined') {
        normalized.set('X-Frontend-Path', (window as any).__currentSpaPath || window.location.pathname);
    }
    return { ...config, method: (config.method || 'GET').toUpperCase(), headers: Object.fromEntries(normalized), baseURL: config.baseURL ?? requestSettings.baseURL };
}

// Only query functions and mutation functions call this transport. It performs
// one HTTP attempt; TanStack Query owns caching, concurrency and operation state.
export async function sendRequest<T = any>(config: RequestConfig): Promise<T> {
    const controller = new AbortController();
    const abort = () => controller.abort();
    config.signal?.addEventListener('abort', abort, { once: true });
    if (config.signal?.aborted) abort();
    const timeout = config.timeout ? setTimeout(abort, config.timeout) : undefined;
    try {
        const headers = new Headers(config.headers);
        const csrf = Cookies.get('XSRF-TOKEN');
        if (csrf && config.csrf !== false) headers.set('X-XSRF-TOKEN', decodeURIComponent(csrf));
        let body: BodyInit | undefined;
        if (config.data instanceof FormData) {
            body = config.data;
            // The browser must write the multipart boundary.
            headers.delete('Content-Type');
        } else if (config.data instanceof Blob || config.data instanceof URLSearchParams || typeof config.data === 'string') {
            body = config.data;
        } else if (config.data !== undefined && config.data !== null) {
            body = JSON.stringify(config.data);
            if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
        }
        const response = await globalThis.fetch(encodeRequestUrl(config), {
            method: config.method, credentials: config.credentials ?? 'include', headers,
            signal: controller.signal, body: config.method === 'GET' || config.method === 'HEAD' ? undefined : body,
        });
        let data: any;
        if (response.ok && config.responseType === 'blob') data = await response.blob();
        else if (response.ok && config.responseType === 'arraybuffer') data = await response.arrayBuffer();
        else {
            const text = await response.text();
            data = text;
            if (config.responseType !== 'text' || !response.ok) {
                try { data = text ? JSON.parse(text) : ''; } catch { /* Non-JSON response. */ }
            }
        }
        const result: HttpResponse = { data, status: response.status, statusText: response.statusText, headers: Object.fromEntries(response.headers), config };
        if (!response.ok) throw new HttpError(`Request failed with status code ${response.status}`, config, result);
        if (config.handleErrors !== false && result.headers['x-supabase-realtime-token']) {
            updateLoggedinUser({
                realtime_token: result.headers['x-supabase-realtime-token'],
                realtime_token_expires_at: result.headers['x-supabase-realtime-token-expires-at'],
                realtime_token_expires_in: result.headers['x-supabase-realtime-token-expires-in'],
            });
        }
        return (config.unwrap === false ? result : data || result) as T;
    } catch (error) {
        if (controller.signal.aborted) throw new RequestCancelledError();
        if (error instanceof HttpError) throw error;
        throw new HttpError(error instanceof Error ? error.message : 'Network Error', config);
    } finally {
        config.signal?.removeEventListener('abort', abort);
        if (timeout) clearTimeout(timeout);
    }
}

const emit = (name: string, detail: unknown) => {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(name, { detail }));
};

// Preserve the application's existing status-specific behavior and form errors.
export async function sendWithPolicy<T = any>(config: RequestConfig): Promise<T> {
    try { return await sendRequest<T>(config); }
    catch (error) {
        if (!(error instanceof HttpError) || config.handleErrors === false) throw error;
        const data: any = error.response?.data;
        if (error.status === 503 && data?.maintenance === true) {
            emit('maintenance:activated', { suspension_type: data.suspension_type || 'full', reason: data.reason || null, suspend_to: data.suspend_to || '', status: 'active', source: 'http-503', event_type: 'maintenance:activated' });
            throw { isMaintenanceMode: true };
        }
        if (error.status === 402 && data?.error === 'INSUFFICIENT_TOKENS' && !config._tokenTopUpRetry) {
            return new Promise<T>((resolve, reject) => {
                let settled = false;
                const abort = () => { settled = true; reject(new RequestCancelledError()); };
                config.signal?.addEventListener('abort', abort, { once: true });
                if (config.signal?.aborted) { abort(); return; }
                emit('tokens:insufficient', {
                    message: data.message || "Top up your token balance to continue.",
                    retry: () => {
                        if (settled) return;
                        settled = true;
                        config.signal?.removeEventListener('abort', abort);
                        sendWithPolicy<T>({ ...config, _tokenTopUpRetry: true }).then(resolve, reject);
                    },
                    cancel: () => {
                        if (settled) return;
                        settled = true;
                        config.signal?.removeEventListener('abort', abort);
                        reject({ isInsufficientTokensCancelled: true });
                    },
                });
            });
        }
        let input = config.data;
        if (input instanceof FormData) input = Object.fromEntries(input);
        else if (typeof input === 'string') { try { input = JSON.parse(input); } catch { input = {}; } }
        if (input?.bypassErrorHandler === 'true') throw error;
        if (typeof data?.details === 'string' && data.details.includes('Realtime contract file not found')) {
            if (data.realtime_token) updateLoggedinUser({ realtime_token: data.realtime_token, realtime_token_expires_at: data.realtime_token_expires_at, realtime_token_expires_in: data.realtime_token_expires_in });
            return data;
        }
        if (error.status === 423 && data?.license_expired) {
            const path = typeof window === 'undefined' ? '' : window.location.pathname;
            if (!['/business/licenses', '/renew-license'].some(prefix => path.startsWith(prefix))) {
                emit('license:expired', { isAdmin: data.is_admin, isTrial: data.is_trial, paymentFailed: data.payment_failed, renewalUrl: '/renew-license', message: data.payment_failed ? 'Your renewal payment failed. Please update your payment method and try again.' : data.is_trial ? 'Your free trial has expired. Please purchase a license to continue.' : 'Your license has expired. Please renew to continue.' });
            }
            throw Object.assign(error, { isLicenseExpired: true });
        }
        if (error.status === 401 && typeof window !== 'undefined') {
            const path = (window as any).__currentSpaPath || window.location.pathname;
            if (!path.startsWith('/checkout/success')) {
                resetPosthog();
                sessionStorage.removeItem('authUser'); sessionStorage.removeItem('homePage');
                setAuthorization(null); window.location.href = '/login';
            }
        }
        throw data?.error || data?.message || error.message;
    }
}
